import React, { useState, useCallback, useMemo, useRef, memo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Grid
} from '@mui/material';
import {
  ArrowForward as DrillDownIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  SortByAlpha as SortIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { formatCommissionAmount } from '../utils/formatters';
import { validateNumericInput } from '../utils/inputValidation';

interface IntermediateUplineTakeDto {
  userId: number;
  username: string;
  fullName: string;
  level: number;
  takePercentage: number;
}

interface HierarchicalOperatorCommissionDto {
  id: number;
  hierarchicalCommissionId: number;
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  ownTakePercentage: number;
  maxAllowedPercentage: number;
  baseAllocationPercentage: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  notes?: string;
  // Calculated fields from backend
  basePoolPercentage: number;
  currentUserTakePercentage: number;
  intermediateUplinesTotalPercentage: number;
  intermediateUplines: IntermediateUplineTakeDto[];
  totalTakenPercentage: number;
  remainingPercentage: number;
  maxCurrentUserCanTake: number;
}

interface HierarchicalDownlineDto {
  id: number;
  userId: number;
  username: string;
  fullName: string;
  email: string;
  parentUserId?: number;
  parentUsername?: string;
  ownTakePercentage: number;
  maxAllowedPercentage: number;
  baseAllocationPercentage: number;
  availableForUplines: number;
  hierarchyLevel: number;
  canCreateDownlines: boolean;
  createdAt: string;
  isActive: boolean;
  notes?: string;
  myTakeFromThem: number;
  remainingForMe: number;
  operatorCommissions: HierarchicalOperatorCommissionDto[];
  downlines: HierarchicalDownlineDto[];
  totalDownlinesCount: number;
  directDownlinesCount: number;
  hasDownlines: boolean;
}

interface NavigationPathItem {
  userId: number;
  username: string;
  fullName: string;
  level: number;
}

interface DownlineDetailsPanelProps {
  downlines: HierarchicalDownlineDto[];
  currentUser: NavigationPathItem;
  onDrillDown: (downline: HierarchicalDownlineDto) => void;
  onEditCommission: (userId: number) => void;
  onRefresh: () => void;
}

type SortField = 'name' | 'level' | 'commission' | 'downlines';
type SortOrder = 'asc' | 'desc';

// Memoized Mobile Operator Card Component - prevents unnecessary re-renders
interface MobileOperatorCardProps {
  operator: HierarchicalOperatorCommissionDto;
  downlineUserId: number;
  iTakeValue: string | number | undefined;
  iTake: number;
  totalTaken: number;
  remaining: number;
  maxCanTake: number;
  onValueChange: (downlineId: number, operatorId: number, value: string, maxValue: number) => void;
  onBlur: (downlineId: number, operatorId: number, maxValue: number) => void;
}

const MobileOperatorCard = memo<MobileOperatorCardProps>(({
  operator,
  downlineUserId,
  iTakeValue,
  iTake,
  totalTaken,
  remaining,
  maxCanTake,
  onValueChange,
  onBlur
}) => {
  return (
    <Grid size={{ xs: 12 }} key={operator.operatorId}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {operator.operatorName}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Base Pool/set:
            </Typography>
            <Chip 
              label={formatCommissionAmount(operator.basePoolPercentage)}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              They Take/set:
            </Typography>
            <Chip 
              label={formatCommissionAmount(operator.ownTakePercentage)}
              size="small"
              color="success"
            />
          </Box>

          {operator.intermediateUplinesTotalPercentage > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Uplines Take/set:
                </Typography>
                {(operator.intermediateUplines || []).length > 0 && (
                  <Tooltip 
                    title={
                      <Box>
                        <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                          {(operator.intermediateUplines || []).length} Intermediate Level{(operator.intermediateUplines || []).length > 1 ? 's' : ''}:
                        </Typography>
                        {(operator.intermediateUplines || []).map(u => (
                          <Typography key={u.userId} variant="caption" sx={{ display: 'block' }}>
                            • L{u.level} - {u.fullName}: {formatCommissionAmount(u.takePercentage)}
                          </Typography>
                        ))}
                      </Box>
                    }
                    arrow
                  >
                    <InfoIcon sx={{ fontSize: 16, color: 'info.main', cursor: 'help' }} />
                  </Tooltip>
                )}
              </Box>
              <Chip 
                label={formatCommissionAmount(operator.intermediateUplinesTotalPercentage)}
                size="small"
                color="secondary"
                variant="outlined"
              />
            </Box>
          )}

          <Box sx={{ bgcolor: 'rgba(255, 152, 0, 0.08)', p: 1.5, borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
              I Take/set:
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="text"
              value={typeof iTakeValue === 'string' ? iTakeValue : iTake.toFixed(2)}
              onChange={(e) => {
                onValueChange(downlineUserId, operator.operatorId, e.target.value, maxCanTake);
              }}
              onBlur={() => {
                onBlur(downlineUserId, operator.operatorId, maxCanTake);
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'white'
                }
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">RM</InputAdornment>,
                inputProps: {
                  style: { textAlign: 'right', fontWeight: 'bold' }
                }
              }}
              helperText={`Max: ${formatCommissionAmount(maxCanTake)}/set`}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: 1, borderColor: 'divider', pt: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              Remaining/set:
            </Typography>
            <Chip 
              label={formatCommissionAmount(remaining)}
              size="small"
              color={remaining > 0 ? 'info' : 'error'}
              sx={{ fontWeight: 'bold' }}
            />
          </Box>

          {totalTaken > 0.6 && (
            <Typography variant="caption" color="error" sx={{ fontStyle: 'italic' }}>
              ⚠️ Total exceeds RM 0.60/set!
            </Typography>
          )}
        </Box>
      </Paper>
    </Grid>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if relevant props changed
  return (
    prevProps.operator.operatorId === nextProps.operator.operatorId &&
    prevProps.iTakeValue === nextProps.iTakeValue &&
    prevProps.iTake === nextProps.iTake &&
    prevProps.totalTaken === nextProps.totalTaken &&
    prevProps.remaining === nextProps.remaining &&
    prevProps.maxCanTake === nextProps.maxCanTake
  );
});

MobileOperatorCard.displayName = 'MobileOperatorCard';

const DownlineDetailsPanel: React.FC<DownlineDetailsPanelProps> = ({
  downlines,
  currentUser,
  onDrillDown,
  onEditCommission,
  onRefresh
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [operatorCommissions, setOperatorCommissions] = useState<Record<number, HierarchicalOperatorCommissionDto[]>>({});
  const [editingOperators, setEditingOperators] = useState<Record<number, Record<number, string | number>>>({});
  const [loadingOperators, setLoadingOperators] = useState<Set<number>>(new Set());
  const [savingOperators, setSavingOperators] = useState<Set<number>>(new Set());
  

  // Filter downlines based on search
  const filteredDownlines = downlines.filter(
    (downline) =>
      downline.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      downline.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      downline.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort downlines
  const sortedDownlines = [...filteredDownlines].sort((a, b) => {
    let compareValue = 0;

    switch (sortField) {
      case 'name':
        compareValue = a.fullName.localeCompare(b.fullName);
        break;
      case 'level':
        compareValue = a.hierarchyLevel - b.hierarchyLevel;
        break;
      case 'commission':
        compareValue = a.ownTakePercentage - b.ownTakePercentage;
        break;
      case 'downlines':
        compareValue = a.totalDownlinesCount - b.totalDownlinesCount;
        break;
    }

    return sortOrder === 'asc' ? compareValue : -compareValue;
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Fetch operator commissions for a downline
  const fetchOperatorCommissions = async (downlineId: number) => {
    try {
      setLoadingOperators(prev => new Set(prev).add(downlineId));
      
      // Fetch downline's operator commissions
      const commissionsResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/downline-operator-commissions/${downlineId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!commissionsResponse.ok) {
        throw new Error('Failed to fetch operator commissions');
      }

      const commissions: HierarchicalOperatorCommissionDto[] = await commissionsResponse.json();
      
      setOperatorCommissions(prev => ({
        ...prev,
        [downlineId]: commissions
      }));

      // Fetch MY operator access (what I actually take from this downline)
      const accessResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/my-operator-downline-access`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const accessData = accessResponse.ok ? await accessResponse.json() : [];

      console.log('🔍 DEBUG: Downline ID:', downlineId);
      console.log('🔍 DEBUG: Access Data from API:', accessData);
      console.log('🔍 DEBUG: Operator Commissions:', commissions);

      // Initialize editing values with backend-calculated values
      const initialValues: Record<number, number> = {};
      commissions.forEach(comm => {
        console.log(`🔍 DEBUG: Operator ${comm.operatorName}:`, {
          downlineId,
          operatorId: comm.operatorId,
          backendCalculatedTake: comm.currentUserTakePercentage, // Already in RM (e.g., 0.3 = RM 0.30/set)
          remaining: comm.remainingPercentage,
          intermediate: comm.intermediateUplinesTotalPercentage
        });
        
        // Use backend-calculated value (already includes database lookup, already in RM)
        initialValues[comm.operatorId] = comm.currentUserTakePercentage; // No multiplication needed
      });
      
      setEditingOperators(prev => ({
        ...prev,
        [downlineId]: initialValues
      }));
      
    } catch (error) {
      console.error('Error fetching operator commissions:', error);
      enqueueSnackbar('Failed to load operator commissions', { variant: 'error' });
    } finally {
      setLoadingOperators(prev => {
        const newSet = new Set(prev);
        newSet.delete(downlineId);
        return newSet;
      });
    }
  };

  // Toggle row expansion
  const handleToggleExpand = (downlineId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(downlineId)) {
      newExpanded.delete(downlineId);
    } else {
      newExpanded.add(downlineId);
      // Fetch operator commissions if not already loaded
      if (!operatorCommissions[downlineId]) {
        fetchOperatorCommissions(downlineId);
      }
    }
    setExpandedRows(newExpanded);
  };

  // Handle edit value change - store raw string for free typing with real-time validation
  const handleEditValueChange = useCallback((downlineId: number, operatorId: number, value: string, maxValue: number) => {
    // Validate input in real-time
    const validatedValue = validateNumericInput(value, maxValue, true, 2);
    
    // Only update if value actually changed (performance optimization)
    setEditingOperators(prev => {
      const currentValue = prev[downlineId]?.[operatorId];
      if (currentValue === validatedValue) {
        return prev; // No change, return same reference to prevent re-render
      }
      
      return {
        ...prev,
        [downlineId]: {
          ...prev[downlineId],
          [operatorId]: validatedValue
        }
      };
    });
  }, []);

  // Handle blur to validate and clamp value - optimized with useCallback
  const handleEditValueBlur = useCallback((downlineId: number, operatorId: number, maxValue: number) => {
    setEditingOperators(prev => {
      const editValues = prev[downlineId] || {};
      const currentValue = editValues[operatorId];
      
      // If it's a string, parse it
      if (typeof currentValue === 'string') {
        const numValue = parseFloat(currentValue);
        if (!isNaN(numValue)) {
          const clamped = Math.max(0, Math.min(maxValue, numValue));
          const formatted = clamped.toFixed(2);
          // Only update if value actually changed
          if (currentValue === formatted) {
            return prev;
          }
          return {
            ...prev,
            [downlineId]: {
              ...prev[downlineId],
              [operatorId]: formatted
            }
          };
        } else {
          // Invalid number, reset to 0
          return {
            ...prev,
            [downlineId]: {
              ...prev[downlineId],
              [operatorId]: '0.00'
            }
          };
        }
      }
      return prev;
    });
  }, []);

  // Save operator commission changes
  const handleSaveOperators = async (downlineId: number) => {
    try {
      setSavingOperators(prev => new Set(prev).add(downlineId));
      
      const userId = localStorage.getItem('userId');
      if (!userId) {
        throw new Error('User ID not found');
      }

      const commissions = operatorCommissions[downlineId];
      const editedValues = editingOperators[downlineId];

      // Save each operator commission
      for (const comm of commissions) {
        const iTakeValue = editedValues[comm.operatorId];
        // Parse if it's a string, otherwise use directly
        const iTake = typeof iTakeValue === 'string' ? parseFloat(iTakeValue) : (iTakeValue || 0);
        
        const updateData = {
          operatorId: comm.operatorId,
          uplineUserId: parseInt(userId),
          downlineUserId: downlineId,
          takePercentage: iTake, // Send amount directly (already in RM, e.g., 0.3 = RM 0.30/set)
          notes: `Updated inline`
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/HierarchicalCommission/update-operator-downline-access`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || `Failed to update ${comm.operatorName}`);
        }
      }

      enqueueSnackbar('Operator commissions updated successfully!', { variant: 'success' });
      
      // Refresh operator commissions
      await fetchOperatorCommissions(downlineId);
      if (onRefresh) {
        onRefresh();
      }
      
    } catch (error) {
      console.error('Error saving operator commissions:', error);
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to save commissions',
        { variant: 'error' }
      );
    } finally {
      setSavingOperators(prev => {
        const newSet = new Set(prev);
        newSet.delete(downlineId);
        return newSet;
      });
    }
  };

  // Cancel editing
  const handleCancelEdit = async (downlineId: number) => {
    // Refetch to get original values from server
    await fetchOperatorCommissions(downlineId);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: { xs: 1, sm: 2 }, maxWidth: '100%', width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" gutterBottom>
              Direct Downlines of {currentUser?.fullName || 'You'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing {sortedDownlines.length} of {downlines.length} downlines
            </Typography>
          </Box>
          <Tooltip title="Refresh data">
            <IconButton onClick={onRefresh} color="primary" size={isMobile ? 'medium' : 'large'}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Search and Sort Controls */}
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by name, username, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 250 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 150 }, flex: { xs: '1 1 calc(50% - 4px)', sm: 'none' } }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortField}
              label="Sort By"
              onChange={(e) => setSortField(e.target.value as SortField)}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="level">Level</MenuItem>
              <MenuItem value="commission">Commission</MenuItem>
              <MenuItem value="downlines">Downlines</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            startIcon={<SortIcon />}
            sx={{ flex: { xs: '1 1 calc(50% - 4px)', sm: 'none' } }}
          >
            {sortOrder === 'asc' ? 'Asc' : 'Desc'}
          </Button>
        </Box>
      </Box>

      {/* Content */}
      {sortedDownlines.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <PeopleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'No downlines found matching your search' : 'No downlines yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm
              ? 'Try adjusting your search criteria'
              : 'This user has not created any downlines yet'}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Desktop Table View */}
          {!isMobile && (
            <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
          <Table stickyHeader sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: 50 }}>
                  Expand
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  onClick={() => handleSort('name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Agent Info
                    {sortField === 'name' && <SortIcon fontSize="small" />}
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  onClick={() => handleSort('level')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                    Level
                    {sortField === 'level' && <SortIcon fontSize="small" />}
                  </Box>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  onClick={() => handleSort('commission')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                    Commission
                    {sortField === 'commission' && <SortIcon fontSize="small" />}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  Operators
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                  onClick={() => handleSort('downlines')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                    Downlines
                    {sortField === 'downlines' && <SortIcon fontSize="small" />}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  Status
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDownlines.map((downline) => {
                const isExpanded = expandedRows.has(downline.userId);
                const isLoading = loadingOperators.has(downline.userId);
                const isSaving = savingOperators.has(downline.userId);
                const operators = operatorCommissions[downline.userId] || [];
                const editValues = editingOperators[downline.userId] || {};

                return (
                  <React.Fragment key={downline.userId}>
                    <TableRow hover>
                      {/* Expand/Collapse Button */}
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleExpand(downline.userId)}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {downline.fullName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{downline.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {downline.email}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={`Level ${downline.hierarchyLevel}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={`Own: ${formatCommissionAmount(downline.ownTakePercentage)}`}
                            size="small"
                            color="success"
                            sx={{ minWidth: 80 }}
                          />
                          <Chip
                            label={`Max: ${formatCommissionAmount(downline.maxAllowedPercentage)}`}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ minWidth: 80 }}
                          />
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={`${downline.operatorCommissions.length} operators`}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Total: {downline.totalDownlinesCount}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Direct: {downline.directDownlinesCount}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Chip
                          label={downline.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={downline.isActive ? 'success' : 'default'}
                        />
                      </TableCell>

                      <TableCell align="center">
                        {downline.hasDownlines && (
                          <Tooltip title="View Their Downlines">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onDrillDown(downline)}
                            >
                              <DrillDownIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row - Operator Commissions */}
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1, bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                            <Typography variant="h6" gutterBottom component="div" sx={{ mb: 1.5 }}>
                              Operator Commissions for {downline.fullName}
                            </Typography>

                            {isLoading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <Typography>Loading operators...</Typography>
                              </Box>
                            ) : operators.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                No operator commissions found.
                              </Typography>
                            ) : (
                              <>
                                {/* Operator Grid */}
                                <Box sx={{ 
                                  display: 'grid', 
                                  gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, 1fr)',
                                    md: 'repeat(3, 1fr)'
                                  },
                                  gap: 2,
                                  mb: 2
                                }}>
                                  {operators.map((operator) => {
                                    // Use backend-calculated values (now amounts in RM per set)
                                    const basePool = operator.basePoolPercentage;
                                    const theyTake = operator.ownTakePercentage;
                                    const intermediateTake = operator.intermediateUplinesTotalPercentage;
                                    const intermediateUplines = operator.intermediateUplines || [];
                                    
                                    // Current edited value, or backend value (already in correct format as amount)
                                    const iTakeValue = editValues[operator.operatorId];
                                    const iTake = typeof iTakeValue === 'string' ? parseFloat(iTakeValue) : (iTakeValue ?? operator.currentUserTakePercentage);
                                    
                                    // Recalculate remaining based on edited value (for real-time feedback)
                                    const totalTaken = theyTake + intermediateTake + iTake;
                                    const remaining = Math.max(0, basePool - totalTaken);
                                    const maxCanTake = operator.maxCurrentUserCanTake;

                                    return (
                                      <Paper key={operator.operatorId} variant="outlined" sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                          {operator.operatorName}
                                        </Typography>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" color="text.secondary">
                                              Base Pool/set:
                                            </Typography>
                                            <Chip 
                                              label={formatCommissionAmount(basePool)}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                              sx={{ height: 20 }}
                                            />
                                          </Box>

                                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" color="text.secondary">
                                              They Take/set:
                                            </Typography>
                                            <Chip 
                                              label={formatCommissionAmount(theyTake)}
                                              size="small"
                                              color="success"
                                              sx={{ height: 20 }}
                                            />
                                          </Box>

                                          {intermediateTake > 0 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                  Uplines Take/set:
                                                </Typography>
                                                {intermediateUplines.length > 0 && (
                                                  <Tooltip 
                                                    title={
                                                      <Box>
                                                        <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                                                          {intermediateUplines.length} Intermediate Level{intermediateUplines.length > 1 ? 's' : ''}:
                                                        </Typography>
                                                        {intermediateUplines.map(u => (
                                                          <Typography key={u.userId} variant="caption" sx={{ display: 'block' }}>
                                                            • L{u.level} - {u.fullName}: {formatCommissionAmount(u.takePercentage)}
                                                          </Typography>
                                                        ))}
                                                      </Box>
                                                    }
                                                    arrow
                                                  >
                                                    <InfoIcon sx={{ fontSize: 14, color: 'info.main', cursor: 'help' }} />
                                                  </Tooltip>
                                                )}
                                              </Box>
                                              <Chip 
                                                label={formatCommissionAmount(intermediateTake)}
                                                size="small"
                                                color="secondary"
                                                variant="outlined"
                                                sx={{ height: 20 }}
                                              />
                                            </Box>
                                          )}

                                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', bgcolor: 'rgba(255, 152, 0, 0.08)', p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                                              <strong>I Take/set:</strong>
                                            </Typography>
                                            <TextField
                                              size="small"
                                              type="text"
                                              value={typeof iTakeValue === 'string' ? iTakeValue : iTake.toFixed(2)}
                                              onChange={(e) => {
                                                // Real-time validation with max value constraint
                                                handleEditValueChange(downline.userId, operator.operatorId, e.target.value, maxCanTake);
                                              }}
                                              onBlur={() => {
                                                // Validate and clamp when user leaves the field
                                                handleEditValueBlur(downline.userId, operator.operatorId, maxCanTake);
                                              }}
                                              sx={{ 
                                                flex: 1,
                                                '& .MuiOutlinedInput-root': {
                                                  bgcolor: 'white'
                                                }
                                              }}
                                              InputProps={{
                                                startAdornment: <InputAdornment position="start">RM</InputAdornment>,
                                                inputProps: {
                                                  style: { textAlign: 'right', fontWeight: 'bold' }
                                                }
                                              }}
                                              helperText={`Max: ${formatCommissionAmount(maxCanTake)}/set`}
                                            />
                                          </Box>

                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: 1, borderColor: 'divider', pt: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                              Remaining/set:
                                            </Typography>
                                            <Chip 
                                              label={formatCommissionAmount(remaining)}
                                              size="small"
                                              color={remaining > 0 ? 'info' : 'error'}
                                              sx={{ height: 20, fontWeight: 'bold' }}
                                            />
                                          </Box>

                                          {totalTaken > 0.6 && (
                                            <Typography variant="caption" color="error" sx={{ fontStyle: 'italic' }}>
                                              ⚠️ Total exceeds RM 0.60/set!
                                            </Typography>
                                          )}
                                        </Box>
                                      </Paper>
                                    );
                                  })}
                                </Box>

                                {/* Action Buttons */}
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                    onClick={() => handleCancelEdit(downline.userId)}
                                    disabled={isSaving}
                                  >
                                    Reset
                                  </Button>
                                  <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={() => handleSaveOperators(downline.userId)}
                                    disabled={isSaving}
                                  >
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                  </Button>
                                </Box>
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
          )}

          {/* Mobile Card View */}
          {isMobile && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {sortedDownlines.map((downline) => {
                const isExpanded = expandedRows.has(downline.userId);
                const isLoading = loadingOperators.has(downline.userId);
                const isSaving = savingOperators.has(downline.userId);
                const operators = operatorCommissions[downline.userId] || [];
                const editValues = editingOperators[downline.userId] || {};

                return (
                  <Card key={downline.userId} sx={{ borderRadius: 2, boxShadow: 2 }}>
                    <CardContent sx={{ p: 0 }}>
                      {/* Header - Always Visible */}
                      <Box
                        onClick={() => handleToggleExpand(downline.userId)}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderBottom: isExpanded ? 1 : 0,
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: 'action.hover'
                          }
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="bold">
                            {downline.fullName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            @{downline.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {downline.email}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleExpand(downline.userId);
                          }}
                          sx={{
                            transition: 'transform 0.2s',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                          }}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>

                      {/* Quick Info - Always Visible */}
                      <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                          label={`Level ${downline.hierarchyLevel}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`Own: ${formatCommissionAmount(downline.ownTakePercentage)}`}
                          size="small"
                          color="success"
                        />
                        <Chip
                          label={`Max: ${formatCommissionAmount(downline.maxAllowedPercentage)}`}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                        <Chip
                          label={downline.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={downline.isActive ? 'success' : 'default'}
                        />
                        <Chip
                          label={`${downline.operatorCommissions.length} operators`}
                          size="small"
                          variant="outlined"
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Total: {downline.totalDownlinesCount} | Direct: {downline.directDownlinesCount}
                          </Typography>
                        </Box>
                        {downline.hasDownlines && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DrillDownIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              onDrillDown(downline);
                            }}
                            sx={{ ml: 'auto' }}
                          >
                            View Downlines
                          </Button>
                        )}
                      </Box>

                      {/* Collapsible Content - Operator Commissions */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="h6" gutterBottom component="div" sx={{ mb: 2 }}>
                            Operator Commissions
                          </Typography>

                          {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                              <Typography>Loading operators...</Typography>
                            </Box>
                          ) : operators.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No operator commissions found.
                            </Typography>
                          ) : (
                            <>
                              {/* Operator Grid - Mobile Optimized with Memoized Cards */}
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                {operators.map((operator) => {
                                  const iTakeValue = editValues[operator.operatorId];
                                  const iTake = typeof iTakeValue === 'string' ? parseFloat(iTakeValue) : (iTakeValue ?? operator.currentUserTakePercentage);
                                  
                                  const totalTaken = operator.ownTakePercentage + operator.intermediateUplinesTotalPercentage + iTake;
                                  const remaining = Math.max(0, operator.basePoolPercentage - totalTaken);
                                  const maxCanTake = operator.maxCurrentUserCanTake;

                                  return (
                                    <MobileOperatorCard
                                      key={operator.operatorId}
                                      operator={operator}
                                      downlineUserId={downline.userId}
                                      iTakeValue={iTakeValue}
                                      iTake={iTake}
                                      totalTaken={totalTaken}
                                      remaining={remaining}
                                      maxCanTake={maxCanTake}
                                      onValueChange={handleEditValueChange}
                                      onBlur={handleEditValueBlur}
                                    />
                                  );
                                })}
                              </Grid>

                              {/* Action Buttons */}
                              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Button
                                  variant="outlined"
                                  startIcon={<CancelIcon />}
                                  onClick={() => handleCancelEdit(downline.userId)}
                                  disabled={isSaving}
                                  fullWidth={isMobile}
                                >
                                  Reset
                                </Button>
                                <Button
                                  variant="contained"
                                  startIcon={<SaveIcon />}
                                  onClick={() => handleSaveOperators(downline.userId)}
                                  disabled={isSaving}
                                  fullWidth={isMobile}
                                >
                                  {isSaving ? 'Saving...' : 'Save Changes'}
                                </Button>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DownlineDetailsPanel;

